import { Box, useDeviceType } from '@packages/shared';
import { useLoginState } from '@packages/utilities';
import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { bridge } from '@empiriecom/mybuy-session/Bridge';
import type { GlycerinCartProduct, GTMEventGlycerinDisplayCart } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import { LineItemFragmentFragmentDoc } from '@packages/gql/generated/shopping/LineItemFragmentFragmentDoc';
import { BasketFragmentFragmentDoc } from '@packages/gql/generated/shopping/BasketFragmentFragmentDoc';
import { BasketEmpty } from './BasketEmpty';
import { DirectOrder } from '../DirectOrder';
import { LineItem } from '../LineItem';
import { BuyBox } from '../BuyBox';
import { JoeLoyalty } from '../JoeLoyalty';
import { PaybackBox } from '../PaybackBox';
import { useEventTracking } from '../Tracking/useEventTracking';
import { buildGlycerinCartProduct } from '../Tracking/buildGlycerinObject';
import { Seals } from '../Seals';

const messages = defineMessages({
  appBasketTitle: {
    id: 'App.Basket.Title',
    defaultMessage: 'Warenkorb',
  },
});

export interface BasketProps {
  data?: FragmentType<typeof BasketFragmentFragmentDoc>;
}

export const Basket: React.FC<BasketProps> = ({ data }) => {
  const fragmentData = getFragmentData(BasketFragmentFragmentDoc, data);
  const { isDesktop } = useDeviceType();
  const { formatMessage } = useIntl();
  const { loggedIn } = useLoginState();
  const { lineItems, loyaltyProgram } = fragmentData || {};
  const dispatch = useEventTracking();
  const dispatchGlycerin = useTracking();

  const isFilledBasket = lineItems && lineItems.length > 0;

  useEffect(() => {
    if (bridge?.setTitle) {
      bridge.setTitle(formatMessage(messages.appBasketTitle));
    }
    dispatch('cart', 'viewPage');
  }, [dispatch, formatMessage]);

  useEffect(() => {
    if (lineItems) {
      const glycerinProducts: GlycerinCartProduct[] = [];
      if (isFilledBasket) {
        lineItems.forEach((lineItem) => {
          const lineItemFragment = getFragmentData(LineItemFragmentFragmentDoc, lineItem);
          glycerinProducts.push(buildGlycerinCartProduct(lineItemFragment));
        });
      }
      dispatchGlycerin<GTMEventGlycerinDisplayCart>({
        event: 'DisplayCart',
        DisplayCartData: {
          products: glycerinProducts,
        },
        _clear: true,
      });
    }
  }, [dispatchGlycerin, isFilledBasket, lineItems]);

  return (
    <>
      {!isFilledBasket && (
        <>
          <BasketEmpty loggedIn={loggedIn} />
          <DirectOrder />
        </>
      )}
      {isFilledBasket && (
        <Box data-testid="filled-basket">
          <Box
            sx={{
              p: {
                xs: 1.5,
                lg: 3,
              },
              bgcolor: 'grey.light',
            }}
          >
            {lineItems?.map((lineItem) => (
              <LineItem
                lineItemData={lineItem}
                key={getFragmentData(LineItemFragmentFragmentDoc, lineItem)?.id}
                lastItem={lineItems.indexOf(lineItem) === lineItems.length - 1}
              />
            ))}
          </Box>
          <Box id="swogo-bundle-0" className="swogo-box" />
          <DirectOrder />
          {!isDesktop && <PaybackBox loyaltyProgramData={loyaltyProgram || {}} />}
          {!isDesktop && <JoeLoyalty loyaltyProgramData={loyaltyProgram || {}} />}
          {!isDesktop && <BuyBox data={fragmentData || undefined} />}
          {!isDesktop && <Seals />}
        </Box>
      )}
    </>
  );
};
